import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { isValidTagLabel } from '@utils-clean/validation';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    upsertRiskAssessmentTag,
    upsertRiskAssessmentTagVariables,
} from '@queries/generated/risk-assessment.generated';
import { UPSERT_RISK_ASSESSMENT_TAG } from '@queries/risk-assessment';

type AddTagDialogProps = {
    studyRef: string;
    onClose: () => void;
    onSubmit: () => void;
    existingTags: Tag[];
};

interface Tag {
    label: string;
    active: boolean;
}

const COMPONENT_NAME = 'add-tag-dialog';

const AddTagDialog = ({ studyRef, onClose, onSubmit, existingTags }: AddTagDialogProps) => {
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'monitoringToolkit',
    });
    const [upsertRiskAssessmentTagMutation] = useMutation<upsertRiskAssessmentTag, upsertRiskAssessmentTagVariables>(
        UPSERT_RISK_ASSESSMENT_TAG,
    );

    const {
        control: textControl,
        handleSubmit,
        formState: { isDirty, isValid },
        watch,
    } = useForm<INewTagForm>({
        mode: 'all',
        defaultValues: { tagLabel: '' },
    });

    useUnsavedChangesTracking({
        area: 'risk-assessment-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'risk-assessment-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    interface INewTagForm {
        tagLabel: string;
    }

    const onlySpacesRegex = /^\s+$/gm;
    const labelIsOnlySpaces = onlySpacesRegex.test(watch('tagLabel'));

    return (
        <DraggableDialog
            dataTestId="create-tag-dialog"
            onClose={onCloseHandler}
            onSubmit={() =>
                handleSubmit((values: INewTagForm) => {
                    if (!values.tagLabel) {
                        return;
                    }
                    upsertRiskAssessmentTagMutation({
                        variables: {
                            riskAssessmentTag: {
                                studyId: studyRef,
                                name: values.tagLabel.toLowerCase(),
                                active: true,
                            },
                        },
                    })
                        .then(async (response) => {
                            if (!response?.data || !response?.data?.upsertRiskAssessmentTag) {
                                ShowError(t('errorMessage'));
                                return;
                            }
                            ShowSuccess(t('actionsSuccessfulToast'));
                            onSubmit();
                        })
                        .catch(() => {
                            ShowError(t('errorMessage'));
                            onClose();
                        });
                })()
            }
            title={mtkTranslation('addTagsModal.title')}
            primaryBtnDisabled={!isDirty || !isValid || labelIsOnlySpaces}
            primaryBtnLabel="Save"
            height={160}
            width={320}
        >
            <TextBoxController
                control={textControl}
                name="tagLabel"
                data-testid="new-tag-label"
                label={mtkTranslation('addTagsModal.inputLabel')}
                maxLength={30}
                required
                validateRules={{
                    noInvalidCharacters: (value) => {
                        if (value && !isValidTagLabel(value as string))
                            return mtkTranslation('addTagsModal.invalidChars');
                        return true;
                    },
                    checkExistingTags: (value) => {
                        if (value) {
                            const trimmed = (value as string).trim().toLowerCase();
                            if (existingTags.some((tag) => tag.label.toLowerCase() === trimmed && tag.active)) {
                                return mtkTranslation('addTagsModal.tagAlreadyExists');
                            }
                            if (existingTags.some((tag) => tag.label.toLowerCase() === trimmed && !tag.active)) {
                                return mtkTranslation('addTagsModal.existingTagInactive');
                            }
                        }
                        return true;
                    },
                }}
            />
        </DraggableDialog>
    );
};

export default AddTagDialog;
